import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ReportFilterBar } from '../../../components/report/ReportFilterBar';
import { useWarehouseReportStore } from '../../../store/warehouse/warehouseReportStore';
import { toast } from 'react-toastify';
import { Trophy, Star, PackageCheck, PackageOpen, Activity, Printer, CalendarDays, BarChart3, Medal, Clock } from 'lucide-react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ComposedChart, Line
} from 'recharts';
import { IconButton } from '../../../components/ButtonComponents';

export const StaffPerformanceReport = () => {
  const reportStore = useWarehouseReportStore();
  const abortRef = useRef<AbortController | null>(null);

  const [filters, setFilters] = useState<any>({
    userIds: [],
    dateRange: [new Date(new Date().setHours(0, 0, 0, 0)), new Date(new Date().setHours(23, 59, 59, 999))],
    operationType: 'ALL',
    warehouseId: null
  });

  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [isDialogVisible, setDialogVisible] = useState(false);

  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);

  const fetchReportData = useCallback(async (silent = false) => {
    // Tarih seçimi henüz tamamlanmadıysa bekle
    if (filters.dateRange && filters.dateRange[0] && !filters.dateRange[1]) {
      return;
    }

    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    if (!silent) setLoading(true);

    try {
      const apiFilters = {
        ...filters,
        userIds: filters.userIds || [],
        userId: filters.userIds?.[0] ?? null,
        // Backend'in beklediği tarih formatına çeviriyoruz
        startDate: filters.dateRange?.[0] ? filters.dateRange[0].toISOString() : undefined,
        endDate: filters.dateRange?.[1] ? filters.dateRange[1].toISOString() : undefined,
      };

      // Store içindeki yeni endpointi çağıran metod (Örn: getStaffPerformanceData)
      const result = await reportStore.getStaffPerformance(apiFilters, {
        signal: controller.signal
      });

      setData({
        items: result.items || [],
        total: result.total || 0
      });

    } catch (error: any) {
      if (error.name === "CanceledError") return;
      toast.error("Performans verileri alınırken hata oluştu.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchReportData();
    }, 200);

    return () => clearTimeout(timeout);
  }, [
    filters.dateRange,
    filters.userIds,
    filters.operationType,
    filters.warehouseId
  ]);

  // Ayın/Haftanın elemanını belirlemek için ilk veriyi alıyoruz (Backend zaten sıralı gönderiyor)
  const topPerformer = data.items.length > 0 ? data.items[0] : null;

  // Genel Toplamları Hesaplama
  const totalCompanyScore = data.items.reduce((acc: number, curr: any) => acc + curr.totalScore, 0);
  const totalCompanyPicks = data.items.reduce((acc: number, curr: any) => acc + curr.totalPickCount, 0);
  const totalCompanyPacks = data.items.reduce((acc: number, curr: any) => acc + curr.totalPackCount, 0);

  return (
    <div className="flex flex-col gap-6 p-4 w-full bg-[var(--bg)] min-h-screen">

      {/* Üst Başlık ve Yazdır Butonu */}
      <div className="flex justify-between items-center print:mb-4">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-[var(--text)] flex items-center gap-2">
            <Activity size={28} className="text-[var(--primary)]" />
            Personel Performans ve Verimlilik Raporu
          </h1>
          <p className="text-sm font-medium text-[var(--text)] opacity-60 mt-1">Personellerin iş bazlı verimliliklerini ve süreç skorlarını analiz edin. Detaylar için satıra tıklayın.</p>
        </div>
        <IconButton
          icon={Printer}
          onClick={() => window.print()}
          className="bg-[var(--card)] border border-[var(--border)] text-[var(--text)] hover:bg-[var(--bg-alt)] shadow-sm print:hidden"
          title="Raporu Yazdır"
        />
      </div>

      {/* Filtre Çubuğu */}
      <div className="print:hidden">
        <ReportFilterBar
          key="staff-performance-filter-bar"
          filters={filters}
          onFilterChange={setFilters}
          loading={loading}
          onRefresh={() => fetchReportData(false)}
        />
      </div>

      {/* Metrik Kartları (KPI) */}
      {!loading && data.items.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 print:grid-cols-4 print:gap-2">
          {/* Günün Lideri */}
          <div className="flex items-center p-4 bg-[var(--card)] border border-[var(--border)] rounded-xl gap-4 shadow-sm relative overflow-hidden print:shadow-none print:border-gray-300">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-yellow-500"></div>
            <div className="p-3 bg-yellow-500/10 text-yellow-600 rounded-full border border-yellow-500/20"><Trophy size={24} /></div>
            <div>
              <h3 className="text-[10px] font-black uppercase tracking-widest text-[var(--text)] opacity-50 mb-1">Dönem Lideri</h3>
              <div className="text-lg font-black text-[var(--text)] leading-none truncate max-w-[150px]">{(topPerformer as any).userName}</div>
              <div className="text-xs font-bold text-yellow-600 mt-1">{(topPerformer as any).totalScore.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Puan</div>
            </div>
          </div>
          {/* Takım Puanı */}
          <div className="flex items-center p-4 bg-[var(--card)] border border-[var(--border)] rounded-xl gap-4 shadow-sm print:shadow-none print:border-gray-300">
            <div className="p-3 bg-[var(--primary)]/10 text-[var(--primary)] rounded-full border border-[var(--primary)]/20"><Star size={24} /></div>
            <div>
              <h3 className="text-[10px] font-black uppercase tracking-widest text-[var(--text)] opacity-50 mb-1">Takım Toplam Puanı</h3>
              <div className="text-xl font-black text-[var(--text)]">{totalCompanyScore.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
          </div>
          {/* Toplama Sayısı */}
          <div className="flex items-center p-4 bg-[var(--card)] border border-[var(--border)] rounded-xl gap-4 shadow-sm print:shadow-none print:border-gray-300">
            <div className="p-3 bg-emerald-500/10 text-emerald-600 rounded-full border border-emerald-500/20"><PackageOpen size={24} /></div>
            <div>
              <h3 className="text-[10px] font-black uppercase tracking-widest text-[var(--text)] opacity-50 mb-1">Tamamlanan Toplama</h3>
              <div className="text-xl font-black text-[var(--text)]">{totalCompanyPicks.toLocaleString('tr-TR')} Görev</div>
            </div>
          </div>
          {/* Paketleme Sayısı */}
          <div className="flex items-center p-4 bg-[var(--card)] border border-[var(--border)] rounded-xl gap-4 shadow-sm print:shadow-none print:border-gray-300">
            <div className="p-3 bg-purple-500/10 text-purple-600 rounded-full border border-purple-500/20"><PackageCheck size={24} /></div>
            <div>
              <h3 className="text-[10px] font-black uppercase tracking-widest text-[var(--text)] opacity-50 mb-1">Tamamlanan Paketleme</h3>
              <div className="text-xl font-black text-[var(--text)]">{totalCompanyPacks.toLocaleString('tr-TR')} Görev</div>
            </div>
          </div>
        </div>
      )}

      {/* Ana Tablo: Performans Sıralaması */}
      <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl overflow-hidden shadow-sm print:shadow-none print:border-gray-300">
        <DataTable
          value={data.items}
          loading={loading}
          onRowClick={(e) => {
            setSelectedUser(e.data);
            setDialogVisible(true);
          }}
          dataKey="userId"
          className="p-datatable-sm cursor-pointer"
          rowClassName={() => 'cursor-pointer hover:bg-[var(--bg-alt)]'}
          rowHover
          sortField="totalScore"
          sortOrder={-1}
          emptyMessage={<div className="p-12 text-center text-[var(--text)] opacity-50 font-medium">Seçilen kriterlere uygun performans verisi bulunamadı.</div>}
        >
          <Column field="userName" header="Personel" sortable body={(r, opt) => (
            <div className="flex items-center gap-3 py-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${opt.rowIndex === 0 ? 'bg-yellow-100 text-yellow-700 border border-yellow-300' : 'bg-[var(--bg-alt)] text-[var(--text)] border border-[var(--border)]'}`}>
                {opt.rowIndex === 0 ? <Medal size={14} /> : `${opt.rowIndex + 1}`}
              </div>
              <div className="flex flex-col">
                <span className={`font-bold ${opt.rowIndex === 0 ? 'text-[var(--text)]' : 'text-[var(--text)] opacity-80'}`}>{r.userName}</span>
                <span className="text-[9px] text-[var(--text)] opacity-50 uppercase tracking-widest">{r.workedDays} Gün Aktif</span>
              </div>
            </div>
          )} />
          <Column field="totalPickCount" header="Toplama (Biten/Devam)" sortable align="center" body={(r) => (
            <div className="flex items-center justify-center gap-1.5">
              <span className="font-bold text-green-600 bg-green-500/10 px-2.5 py-1 rounded-lg border border-green-500/20" title="Tamamlanan Toplama Görevleri">{(r.totalPickCount || 0).toLocaleString('tr-TR')}</span>
              <span className="text-[var(--text)] opacity-30 text-xs">/</span>
              <span className="font-bold text-orange-500 bg-orange-500/10 px-2.5 py-1 rounded-lg border border-orange-500/20" title="Devam Eden Toplama Görevleri">{(r.ongoingPickCount || 0).toLocaleString('tr-TR')}</span>
            </div>
          )} />
          <Column field="totalPackCount" header="Paketleme (Biten/Devam)" sortable align="center" body={(r) => (
            <div className="flex items-center justify-center gap-1.5">
              <span className="font-bold text-green-600 bg-green-500/10 px-2.5 py-1 rounded-lg border border-green-500/20" title="Tamamlanan Paketleme Görevleri">{(r.totalPackCount || 0).toLocaleString('tr-TR')}</span>
              <span className="text-[var(--text)] opacity-30 text-xs">/</span>
              <span className="font-bold text-orange-500 bg-orange-500/10 px-2.5 py-1 rounded-lg border border-orange-500/20" title="Devam Eden Paketleme Görevleri">{(r.ongoingPackCount || 0).toLocaleString('tr-TR')}</span>
            </div>
          )} />
          <Column field="averageTaskDurationMins" header="Ort. Süre" sortable align="center" body={(r) => (
            <div className="flex flex-col items-center">
              <span className="font-bold text-[var(--text)] opacity-90 flex items-center gap-1"><Clock size={12} className="text-[var(--primary)]" /> {(r.averageTaskDurationMins || 0).toLocaleString('tr-TR')} dk</span>
              <span className="text-[9px] text-[var(--text)] opacity-50 font-medium">Görev Başına</span>
            </div>
          )} />
          <Column field="totalItemsHandled" header="İşlenen Ürün & Ort." sortable align="center" body={(r) => (
            <div className="flex flex-col items-center">
              <span className="font-bold text-[var(--text)] opacity-90">{r.totalItemsHandled.toLocaleString('tr-TR')}</span>
              <span className="text-[9px] text-[var(--text)] opacity-50 font-medium">Ort: {r.averageDailyItems.toLocaleString('tr-TR')}/gün</span>
            </div>
          )} />
          <Column field="totalScore" header="Zorluk Puanı & Ort." sortable align="right" body={(r) => (
            <div className="flex flex-col items-end">
              <span className="font-black text-lg text-[var(--primary)]">{r.totalScore.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              <span className="text-[9px] text-[var(--text)] opacity-50 font-bold uppercase tracking-wider">Ort: {r.averageDailyScore.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/gün</span>
            </div>
          )} />
        </DataTable>
      </div>

      {/* Görsel Grafik Analizi */}
      {!loading && data.items.length > 0 && (
        <div className="w-full bg-[var(--card)] border border-[var(--border)] rounded-xl p-6 shadow-sm flex flex-col mt-4 print:break-before-page print:shadow-none print:border-gray-300">
          <h3 className="text-lg font-black text-[var(--text)] flex items-center gap-2 uppercase tracking-tight">
            <BarChart3 size={20} className="text-[var(--primary)]" />
            Görsel Performans Karşılaştırması
          </h3>
          <div className="w-full h-[400px] mt-6">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.items} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" opacity={0.4} />
                <XAxis dataKey="userName" axisLine={false} tickLine={false} tick={{ fill: 'var(--text)', fontSize: 12, opacity: 0.8 }} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: 'var(--text)', fontSize: 12, opacity: 0.8 }} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)', borderRadius: '8px', color: 'var(--text)' }}
                  itemStyle={{ fontSize: '13px', fontWeight: 'bold' }}
                  cursor={{ fill: 'var(--border)', opacity: 0.1 }}
                  formatter={(value: number | string | readonly (string | number)[] | undefined) => {
                    if (Array.isArray(value)) {
                      const first = value[0];
                      return typeof first === 'number'
                        ? first.toLocaleString('tr-TR', { maximumFractionDigits: 2 })
                        : first ?? '';
                    }
                    return typeof value === 'number'
                      ? value.toLocaleString('tr-TR', { maximumFractionDigits: 2 })
                      : value ?? '';
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '12px', fontWeight: 600, paddingTop: '10px' }} />
                <Bar dataKey="totalScore" name="Genel Performans Puanı" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={40} />
                <Bar dataKey="totalPickCount" name="Toplama Görevi" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={40} />
                <Bar dataKey="totalPackCount" name="Paketleme Görevi" fill="#8b5cf6" radius={[4, 4, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <Dialog
        header={
          <div className="flex items-center gap-2 text-[var(--text)]">
            <Activity className="text-[var(--primary)]" />
            <span className="font-black tracking-tight">{selectedUser?.userName} - Performans Detayları</span>
          </div>
        }
        visible={isDialogVisible}
        style={{ width: '90vw', maxWidth: '1000px' }}
        onHide={() => setDialogVisible(false)}
        maximizable
        className="dark:bg-[var(--card)]"
        contentClassName="bg-[var(--bg)]"
        headerClassName="bg-[var(--card)] border-b border-[var(--border)]"
      >
        {selectedUser && (
          <div className="flex flex-col gap-6 pt-4">
            {/* Grafik Kartı */}
            <div className="w-full bg-[var(--card)] border border-[var(--border)] rounded-xl p-6 shadow-sm flex flex-col">
              <h3 className="text-lg font-black text-[var(--text)] flex items-center gap-2 uppercase tracking-tight mb-6">
                <BarChart3 size={20} className="text-[var(--primary)]" />
                Günlük İlerleme ve Zorluk Puanı
              </h3>
              <div className="w-full h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={selectedUser.dailyStats} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" opacity={0.4} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(v) => new Date(v).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' })}
                      axisLine={false}
                      tickLine={false}
                      dy={10}
                      tick={{ fill: 'var(--text)', fontSize: 12, opacity: 0.8 }}
                    />
                    <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{ fill: 'var(--text)', fontSize: 12, opacity: 0.8 }} />
                    <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{ fill: 'var(--text)', fontSize: 12, opacity: 0.8 }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)', borderRadius: '8px', color: 'var(--text)' }}
                      labelFormatter={(label) => new Date(label).toLocaleDateString('tr-TR')}
                      itemStyle={{ fontSize: '13px', fontWeight: 'bold' }}
                      cursor={{ fill: 'var(--border)', opacity: 0.1 }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px', fontWeight: 600, paddingTop: '10px' }} />
                    <Bar yAxisId="left" dataKey="picks" name="Toplanan" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={40} />
                    <Bar yAxisId="left" dataKey="packs" name="Paketlenen" fill="#8b5cf6" radius={[4, 4, 0, 0]} maxBarSize={40} />
                    <Line yAxisId="right" type="monotone" dataKey="score" name="Zorluk Puanı" stroke="#f59e0b" strokeWidth={3} dot={{ r: 4, fill: '#f59e0b', strokeWidth: 2 }} activeDot={{ r: 6 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Günlük Veri Tablosu */}
            <div className="w-full bg-[var(--card)] border border-[var(--border)] rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 border-b border-[var(--border)] bg-[var(--bg-alt)] flex items-center gap-2">
                <CalendarDays size={18} className="text-[var(--primary)]" />
                <h3 className="text-sm font-bold text-[var(--text)] uppercase tracking-widest opacity-80">
                  Günlük Performans Dökümü
                </h3>
              </div>
              <DataTable value={selectedUser.dailyStats} className="p-datatable-sm" emptyMessage="Günlük veri bulunamadı." stripedRows rowHover>
                <Column field="date" header="Tarih" sortable body={(r) => <span className="font-semibold text-[var(--text)]">{new Date(r.date).toLocaleDateString('tr-TR')}</span>} />
                <Column field="picks" header="Toplanan" sortable body={(r) => <span className="font-bold text-blue-600">{r.picks.toLocaleString('tr-TR')} İşlem</span>} />
                <Column field="packs" header="Paketlenen" sortable body={(r) => <span className="font-bold text-purple-600">{r.packs.toLocaleString('tr-TR')} İşlem</span>} />
                <Column field="items" header="İşlenen Ürün" sortable body={(r) => <span className="font-medium text-[var(--text)] opacity-80">{r.items.toLocaleString('tr-TR')} Adet</span>} />
                <Column field="score" header="Günlük Zorluk/Puan" sortable body={(r) => <span className="font-black text-[var(--primary)]">{r.score.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>} align="right" />
              </DataTable>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};